<script lang="ts">
  import * as ContextMenu from '$lib/components/ui/context-menu';
  import { Button } from '$lib/components/ui/button';
  import Avatar from '../Avatar.svelte';
  import ReadReceiptAvatars from '../ReadReceiptAvatars.svelte';
  import WhatsAppIcon from '../WhatsAppIcon.svelte';
  import { messageTimeLabel } from './formatting';
  import type { ChatMessage, MessagingUser } from './api';
  import AttachmentGrid from './AttachmentGrid.svelte';
  import ReactionsRow from './ReactionsRow.svelte';
  import DeleteConfirmDialog from './DeleteConfirmDialog.svelte';
  import MessageActionsMenu from './MessageActionsMenu.svelte';

  export let message: ChatMessage;
  export let mine: boolean;
  export let groupStart: boolean;
  export let groupEnd: boolean;
  export let sender: MessagingUser | undefined;
  // Show the sender's name above their first bubble in a run -- only meaningful in group
  // chats, where an avatar alone doesn't disambiguate who's speaking.
  export let showSenderName: boolean = false;
  // Group chats can have several readers, so they get an avatar list (each reader shown
  // once, on their most recent read message -- computed by the caller); a 1:1 DM only ever
  // has one other person, so it keeps the simpler "seen" dot instead.
  export let isGroup: boolean = false;
  export let readers: MessagingUser[] = [];
  export let currentUserId: number;

  export let onReply: (message: ChatMessage) => void = () => {};
  export let onEdit: (message: ChatMessage) => void = () => {};
  export let onDelete: (message: ChatMessage) => void = () => {};
  export let onReact: (message: ChatMessage, emoji: string) => void = () => {};
  export let onPin: (message: ChatMessage) => void = () => {};
  export let onUnpin: (message: ChatMessage) => void = () => {};
  export let onForward: (message: ChatMessage) => void = () => {};
  export let onJumpTo: (messageId: string) => void = () => {};

  let openDeleteDialog = false;
  let contextMenuOpen = false;
  let contextMenuTriggerEl: HTMLDivElement;

  // Lets the "..." button open the exact same menu a right-click/long-press on the bubble would,
  // rather than maintaining a second, separately-wired menu -- same technique used by
  // CalendarActivityCard.svelte's own "..." overlay buttons. Event modifiers (|stopPropagation)
  // only work on native DOM elements, not on a component's forwarded `on:click` -- Button is the
  // latter, so the stop happens here instead.
  function openContextMenu(e: MouseEvent) {
    e.stopPropagation();
    contextMenuTriggerEl?.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, clientX: e.clientX, clientY: e.clientY }));
  }

  function requestDelete() {
    contextMenuOpen = false;
    openDeleteDialog = true;
  }
</script>

<ContextMenu.Root bind:open={contextMenuOpen} portal="#shadcn-ui-dialog-target">
  <ContextMenu.Trigger>
    <div bind:this={contextMenuTriggerEl} id="message-{message.id}" class="flex items-end gap-2 {mine ? 'flex-row-reverse' : ''} {groupStart ? 'mt-4' : 'mt-0.5'}">
      {#if !mine}
        <div class="w-8 shrink-0 self-end">
          {#if groupEnd && sender}
            <Avatar profile_photo={sender.profile_photo} first_name={sender.first_name} last_name={sender.last_name} username={sender.username} shape="circle" pad_right={false} />
          {/if}
        </div>
      {/if}
      <div class="flex max-w-[75%] flex-col {mine ? 'items-end' : 'items-start'}">
        {#if showSenderName && groupStart && !mine && sender}
          <span class="mb-1 px-1 text-xs font-medium text-primary">{sender.first_name} {sender.last_name}</span>
        {/if}
        <div class="group flex items-center gap-1 {mine ? 'flex-row-reverse' : ''}">
          <div class="rounded-lg px-3.5 py-2 text-sm leading-relaxed {mine ? 'bg-primary text-primary-foreground' : 'border border-border bg-muted/60 text-foreground'}">
            {#if message.deleted_at}
              <p class="m-0 italic text-muted-foreground {mine ? 'text-primary-foreground/70' : ''}">This message was deleted</p>
            {:else}
              {#if message.forwarded_from}
                <div class="mb-1 flex items-center gap-1 text-xs opacity-70">
                  <i class="fa-solid fa-share"></i>
                  <span>Forwarded from {message.forwarded_from.first_name} {message.forwarded_from.last_name}</span>
                </div>
              {/if}
              {#if message.reply_to}
                <button
                  type="button"
                  class="mb-1 block w-full rounded border-l-2 border-current/40 bg-black/5 px-2 py-1 text-left text-xs opacity-80 hover:opacity-100"
                  on:click={() => message.reply_to && onJumpTo(message.reply_to.id)}
                >
                  {#if message.reply_to.deleted}
                    <span class="italic">Original message was deleted</span>
                  {:else}
                    <span class="line-clamp-2 break-words">{message.reply_to.body ?? '[attachment]'}</span>
                  {/if}
                </button>
              {/if}
              {#if message.attachments.length > 0}
                <div class="mb-1 flex flex-wrap gap-1">
                  <AttachmentGrid attachments={message.attachments} />
                </div>
              {/if}
              {#if message.body}
                <p class="m-0 whitespace-pre-wrap break-words">{message.body}</p>
              {/if}
            {/if}
          </div>

          {#if !message.deleted_at}
            {#if message.via_whatsapp}
              <WhatsAppIcon size={12} />
            {/if}
            <Button
              size="icon"
              variant="ghost"
              class="h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              on:click={openContextMenu}
            >
              <i class="fa-solid fa-ellipsis text-xs"></i>
            </Button>
          {/if}
        </div>

        {#if message.reactions.length > 0}
          <div class="mt-1 px-1">
            <ReactionsRow reactions={message.reactions} {currentUserId} onReact={(emoji) => onReact(message, emoji)} />
          </div>
        {/if}

        {#if groupEnd}
          <div class="mt-1 flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground">
            {#if message.pinned_at}<i class="fa-solid fa-thumbtack" title="Pinned"></i>{/if}
            <span>{messageTimeLabel(message.created_at)}</span>
            {#if message.edited_at && !message.deleted_at}<span>(edited)</span>{/if}
            {#if mine && isGroup && readers.length > 0}
              <ReadReceiptAvatars {readers} />
            {:else if mine && !isGroup}
              <i
                class="fa-solid fa-check-double text-sm leading-none {message.read_by.length > 0 ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}"
                style="text-shadow: 0 0 0.4px currentColor;"
                title={message.read_by.length > 0 ? 'Read' : 'Sent'}
              ></i>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </ContextMenu.Trigger>
  {#if !message.deleted_at}
    <ContextMenu.Content>
      <MessageActionsMenu
        reactions={message.reactions}
        {currentUserId}
        {mine}
        pinned={!!message.pinned_at}
        onReply={() => onReply(message)}
        onForward={() => onForward(message)}
        onPin={() => onPin(message)}
        onUnpin={() => onUnpin(message)}
        onReact={(emoji) => onReact(message, emoji)}
        onEdit={() => onEdit(message)}
        onRequestDelete={requestDelete}
      />
    </ContextMenu.Content>
  {/if}
</ContextMenu.Root>

<DeleteConfirmDialog bind:open={openDeleteDialog} entityNoun="message" containerNoun="conversation" onConfirm={() => onDelete(message)} />
